"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import { Menu as MenuIcon, X, ChevronDown, ChevronRight } from "lucide-react";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { LocaleCode } from "@/lib/labels/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { Icon } from "@/components/ui/Icon";
import { idToUrlSegment } from "@/utilities/mappers/idUrlFormat";
import type { HeaderMenuItem } from "./headerModel";
interface MobileMenuProps {
  /** Primary menu items */
  items: HeaderMenuItem[];
  basePath: string;
  enabledLocales: LocaleCode[];
  showLangSwitcher: boolean;
  /** Logo URL to display in the menu header */
  logoUrl?: string | null;
  /** Tenant name as fallback if no logo */
  tenantName?: string;
  /** Button/icon color variant and panel style */
  variant?: "light" | "dark";
  /** Panel background style - matches header type */
  panelStyle?: "default" | "branded";
  /** Text mode for branded panels - light text for dark backgrounds, dark text for light backgrounds */
  textMode?: "light" | "dark";
  /** Additional elements to render in the menu (e.g., AuthControls) */
  additionalContent?: React.ReactNode;
  /** Icon menu items to display at the top of the drawer */
  iconItems?: HeaderMenuItem[];
}
export function MobileMenu(t0) {
  const $ = _c(33);
  const {
    items,
    basePath,
    enabledLocales,
    showLangSwitcher,
    logoUrl,
    tenantName,
    variant: t1,
    panelStyle: t2,
    textMode: t3,
    additionalContent,
    iconItems: t4
  } = t0;
  const variant = t1 === undefined ? "dark" : t1;
  const panelStyle = t2 === undefined ? "default" : t2;
  const textMode = t3 === undefined ? "light" : t3;
  let t5;
  if ($[0] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const iconItems = t5;
  const [isOpen, setIsOpen] = useState(false);
  const [expandedMegaMenu, setExpandedMegaMenu] = useState(null);
  const [mounted, setMounted] = useState(false);
  const labels = useLabels();
  let t6;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setMounted(true);
    t7 = [];
    $[2] = t6;
    $[3] = t7;
  } else {
    t6 = $[2];
    t7 = $[3];
  }
  useEffect(t6, t7);
  const iconColor = variant === "light" ? "text-white" : "text-foreground";
  let t8;
  if ($[4] !== panelStyle || $[5] !== textMode) {
    const getPanelStyles = () => {
      if (panelStyle === "default") {
        return {
          panel: "bg-background",
          header: "border-border",
          text: "text-foreground",
          textMuted: "text-muted-foreground",
          hover: "hover:bg-muted",
          border: "border-border",
          logoFallback: "text-primary"
        };
      }
      if (textMode === "light") {
        return {
          panel: "",
          header: "border-white/20",
          text: "text-white",
          textMuted: "text-white/70",
          hover: "hover:bg-white/10",
          border: "border-white/20",
          logoFallback: "text-white"
        };
      } else {
        return {
          panel: "",
          header: "border-foreground/20",
          text: "text-foreground",
          textMuted: "text-muted-foreground",
          hover: "hover:bg-foreground/10",
          border: "border-foreground/20",
          logoFallback: "text-foreground"
        };
      }
    };
    t8 = getPanelStyles();
    $[4] = panelStyle;
    $[5] = textMode;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const styles = t8;
  let t9;
  if ($[7] !== panelStyle) {
    t9 = panelStyle === "branded" ? {
      background: "linear-gradient(to bottom, var(--header-gradient-from), var(--header-gradient-to))"
    } : undefined;
    $[7] = panelStyle;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const panelBgStyle = t9;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIsOpen(true);
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = `md:hidden p-2 -mr-2 ${iconColor}`;
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <MenuIcon className="w-6 h-6" />;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== t11) {
    t13 = <button onClick={t10} className={t11} aria-label="Open menu">{t12}</button>;
    $[11] = t11;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== additionalContent || $[14] !== basePath || $[15] !== enabledLocales || $[16] !== expandedMegaMenu || $[17] !== iconItems || $[18] !== isOpen || $[19] !== items || $[20] !== labels || $[21] !== logoUrl || $[22] !== mounted || $[23] !== panelBgStyle || $[24] !== panelStyle || $[25] !== showLangSwitcher || $[26] !== styles || $[27] !== tenantName || $[28] !== textMode) {
    t14 = mounted && createPortal(<>{isOpen && <div className="fixed inset-0 bg-black/50 z-[9998] md:hidden" onClick={() => setIsOpen(false)} />}<div className={`fixed top-0 right-0 h-full w-72 max-w-[85vw] ${styles.panel} ${styles.text} shadow-xl z-[9999] transform transition-transform duration-300 ease-in-out md:hidden ${isOpen ? "translate-x-0" : "translate-x-full"}`} style={panelBgStyle}><div className={`flex items-center justify-between p-4 border-b ${styles.header}`}><Link href={basePath || "/"} prefetch={false} onClick={() => setIsOpen(false)}>{logoUrl ? <Image src={logoUrl} alt={tenantName || "Home"} width={120} height={32} className="h-8 w-auto object-contain" unoptimized={true} /> : <span className={`font-semibold ${styles.logoFallback}`}>{tenantName || "Home"}</span>}</Link><button onClick={() => setIsOpen(false)} className={`p-2 -mr-2 ${styles.text}`} aria-label="Close menu"><X className="w-6 h-6" /></button></div><nav className="p-4 overflow-y-auto max-h-[calc(100vh-80px)]">{(() => {
            const drawerIconItems = iconItems.filter(_temp);
            if (drawerIconItems.length === 0) {
              return null;
            }
            return <div className={`flex items-center gap-2 pb-4 mb-4 border-b ${styles.border}`}>{drawerIconItems.map((item_0, index) => <Link key={index} href={item_0.url.startsWith("http") ? item_0.url : `${basePath}${item_0.url}`} prefetch={false} target={item_0.openInNewTab ? "_blank" : undefined} onClick={() => setIsOpen(false)} className={`flex items-center gap-2 py-2 px-3 ${styles.text} ${styles.hover} rounded-lg transition text-sm font-medium`} aria-label={item_0.label}><Icon node={item_0.iconNode} size={18} />{item_0.label}</Link>)}</div>;
          })()}<ul className="space-y-1">{items.map((item_1, index_0) => {
              const competitionItems = item_1.megaMenu?.competitions ?? [];
              const teamItems = item_1.megaMenu?.teams ?? [];
              const isExpanded = expandedMegaMenu === index_0;
              return <li key={index_0}>{item_1.megaMenu ? <><button onClick={() => setExpandedMegaMenu(isExpanded ? null : index_0)} className={`w-full flex items-center justify-between py-3 px-4 ${styles.text} ${styles.hover} rounded-lg transition font-medium`}><span className="flex items-center gap-2">{item_1.iconNode && <Icon node={item_1.iconNode} size={18} />}{item_1.label}</span>{isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>{isExpanded && <div className={`mt-2 ml-4 border-l-2 ${styles.border} pl-4 space-y-4`}>{competitionItems.length > 0 && <div><div className={`text-xs font-semibold mb-2 ${styles.textMuted} uppercase tracking-wide`}>{labels.competitions.popularCompetitions}</div><div className="space-y-2">{competitionItems.map(comp => {
                          const resolvedLogo = comp.logo;
                          return <Link key={comp.entityId} prefetch={false} href={`${basePath}/football/competitions/${idToUrlSegment(comp.entityId)}`} onClick={() => setIsOpen(false)} className={`flex items-center gap-3 py-2 px-3 ${styles.hover} rounded-lg transition`}>{resolvedLogo && <Image src={resolvedLogo} alt={comp.label || ""} width={32} height={32} className="w-8 h-8 object-contain shrink-0" />}<span className={`text-sm ${styles.text} truncate`}>{comp.label || comp.entityId}</span></Link>;
                        })}</div></div>}{teamItems.length > 0 && <div className={`pt-4 border-t ${styles.border}`}><div className="flex flex-wrap gap-2">{teamItems.map(team => <Link key={team.entityId} prefetch={false} href={`${basePath}/football/teams/${idToUrlSegment(team.entityId)}`} onClick={() => setIsOpen(false)} className="shrink-0 hover:opacity-70 transition-opacity" title={team.label || team.entityId}>{team.logo && <Image src={team.logo} alt={team.label || ""} width={40} height={40} className="w-10 h-10 object-contain" />}</Link>)}</div></div>}</div>}</> : <><Link href={item_1.url.startsWith("http") ? item_1.url : `${basePath}${item_1.url}`} prefetch={false} target={item_1.openInNewTab ? "_blank" : undefined} onClick={() => setIsOpen(false)} className={`flex items-center gap-2 py-3 px-4 ${styles.text} ${styles.hover} rounded-lg transition font-medium`}>{item_1.iconNode && <Icon node={item_1.iconNode} size={18} />}{item_1.label}</Link>{item_1.children.length > 0 && <ul className={`ml-4 mt-1 space-y-1 border-l-2 ${styles.border} pl-4`}>{item_1.children.map((child, childIndex) => <li key={childIndex}><Link href={`${basePath}${child.url}`} prefetch={false} target={child.openInNewTab ? "_blank" : undefined} onClick={() => setIsOpen(false)} className={`block py-2 px-4 text-sm ${styles.textMuted} hover:${styles.text} ${styles.hover} rounded-lg transition`}>{child.label}</Link></li>)}</ul>}</>}</li>;
            })}</ul>{showLangSwitcher && <div className={`mt-6 pt-6 border-t ${styles.border}`}><span className={`block text-sm ${styles.textMuted} mb-2 px-4`}>Language</span><LanguageSwitcher enabledLocales={enabledLocales} variant="list" textMode={panelStyle === "branded" ? textMode : "dark"} /></div>}{additionalContent && <div className={`mt-6 pt-6 border-t ${styles.border} px-4`}>{additionalContent}</div>}</nav></div></>, document.body);
    $[13] = additionalContent;
    $[14] = basePath;
    $[15] = enabledLocales;
    $[16] = expandedMegaMenu;
    $[17] = iconItems;
    $[18] = isOpen;
    $[19] = items;
    $[20] = labels;
    $[21] = logoUrl;
    $[22] = mounted;
    $[23] = panelBgStyle;
    $[24] = panelStyle;
    $[25] = showLangSwitcher;
    $[26] = styles;
    $[27] = tenantName;
    $[28] = textMode;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}
function _temp(item) {
  return item.iconNode;
}